import { Box } from "@chakra-ui/react";
import React, { ReactNode } from "react";
import { useParams } from "react-router-dom";
import { ServiceManagementFeatures } from "../../enums/ServiceManagementFeatures";
import { GetIntegrationMMSListParameterForm } from "../../forms/GetIntegrationMMSListParameterForm";
import { GetIntegrationWorkerListParameterForm } from "../../forms/GetIntegrationWorkerListParameterForm";
import { GetIntegrationWorkerStatusParameterForm } from "../../forms/GetIntegrationWorkerStatusParameterForm";
import { useAdminApiRequest } from "../../hooks/useApiRequest";
import { IntegrationFeature } from "../../pages/Integration/IntegrationDetail";

export interface IntegrationGetMMSListParameter {
  type: string;
}

export interface IntegrationGetWorkerListParameter {
  listTypeId: string;
}

export interface IntegrationGetWorkerStatusParameter {
  executionId: string;
}

export interface IntegrationFeatureProps {
  businessId: string | undefined;
  integrationId: string | undefined;
  feature: IntegrationFeature;
  setFieldFeatureValue: (fieldName: string, value: any) => void;
}

interface IntegrationFeatureParameterProps {
  feature: IntegrationFeature;
  onParameterChange?: (fieldName: string, value: any) => void;
}

const WrapParameter = ({ children }: { children: ReactNode }) => (
  <Box w="100%" pt={1} pb={2}>
    {children}
  </Box>
);

const applyParameterChange = (
  feature: IntegrationFeature,
  onParameterChange: ((fieldName: string, value: any) => void) | undefined,
  fieldName: string,
  value: any
) => {
  if (!feature.parameter) {
    feature.parameter = {};
  }
  feature.parameter[fieldName] = value;
  onParameterChange?.(fieldName, value);
};

const IntegrationMMSListParameter = ({
  feature,
  onParameterChange,
}: IntegrationFeatureParameterProps) => {
  const { businessId, integrationId } = useParams();
  const { data: mmsListTypes, isLoading: mmsListTypesIsLoading } =
    useAdminApiRequest(
      (apiClient) => {
        if (!businessId || !integrationId) {
          return null;
        }
        return apiClient.getIntegrationMMSListTypes(businessId, integrationId);
      },
      [businessId, integrationId]
    );

  return (
    <WrapParameter>
      <GetIntegrationMMSListParameterForm
        businessId={businessId}
        integrationId={integrationId}
        feature={feature}
        setFieldFeatureValue={(fieldName, value) =>
          applyParameterChange(feature, onParameterChange, fieldName, value)
        }
        types={mmsListTypes}
        typesIsLoading={mmsListTypesIsLoading}
      />
    </WrapParameter>
  );
};

const IntegrationWorkerListParameter = ({
  feature,
  onParameterChange,
}: IntegrationFeatureParameterProps) => {
  const { businessId, integrationId } = useParams();
  const { data: workerListTypes, isLoading: workerListTypesIsLoading } =
    useAdminApiRequest(
      (apiClient) => {
        if (!businessId || !integrationId) {
          return null;
        }
        return apiClient.getIntegrationWorkerListTypes(
          businessId,
          integrationId
        );
      },
      [businessId, integrationId]
    );

  return (
    <WrapParameter>
      <GetIntegrationWorkerListParameterForm
        businessId={businessId}
        integrationId={integrationId}
        feature={feature}
        setFieldFeatureValue={(fieldName, value) =>
          applyParameterChange(feature, onParameterChange, fieldName, value)
        }
        types={workerListTypes}
        typesIsLoading={workerListTypesIsLoading}
      />
    </WrapParameter>
  );
};

const IntegrationWorkerStatusParameter = ({
  feature,
  onParameterChange,
}: IntegrationFeatureParameterProps) => {
  const { businessId, integrationId } = useParams();
  const { data: workerStatus, isLoading: workerStatusIsLoading } =
    useAdminApiRequest(
      (apiClient) => {
        if (!businessId || !integrationId) {
          return null;
        }
        return apiClient.getIntegrationWorkerStatusAll(
          businessId,
          integrationId
        );
      },
      [businessId, integrationId]
    );

  return (
    <WrapParameter>
      <GetIntegrationWorkerStatusParameterForm
        businessId={businessId}
        integrationId={integrationId}
        feature={feature}
        setFieldFeatureValue={(fieldName, value) =>
          applyParameterChange(feature, onParameterChange, fieldName, value)
        }
        workerStatus={
          workerStatus ? Object.entries(workerStatus).map(([key]) => key) : []
        }
        workerStatusIsLoading={workerStatusIsLoading}
      />
    </WrapParameter>
  );
};

export const IntegrationFeatureParameter = ({
  feature,
  onParameterChange,
}: IntegrationFeatureParameterProps) => {
  switch (feature.name) {
    case ServiceManagementFeatures.GetIntegrationMMSList:
      return (
        <IntegrationMMSListParameter
          feature={feature}
          onParameterChange={onParameterChange}
        />
      );
    case ServiceManagementFeatures.GetIntegrationWorkerList:
      return (
        <IntegrationWorkerListParameter
          feature={feature}
          onParameterChange={onParameterChange}
        />
      );
    case ServiceManagementFeatures.GetIntegrationWorkerStatus:
      return (
        <IntegrationWorkerStatusParameter
          feature={feature}
          onParameterChange={onParameterChange}
        />
      );
    default:
      return <></>;
  }
};
